import React, { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { api } from 'common';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { C2Select, C2SummaryCard } from '../components/Table/C2UserTable';
import C2ReportTable, { C2Cell, formatCop } from '../components/reports/C2ReportTable';

const copColumn = (title, field, extra = {}) => ({
  title,
  field,
  type: 'numeric',
  render: (r) => <C2Cell primary={formatCop(r[field])} strong={false} />,
  ...extra,
});

export default function Earningreports({ bookings, loading }) {
  const rows = useMemo(() => api.buildPlatformEarnings(bookings || []), [bookings]);
  const years = useMemo(() => {
    const ys = [...new Set(rows.map((r) => r.monthKey.slice(0, 4)))].sort((a, b) => b.localeCompare(a));
    return [{ value: '', label: 'Todos los años' }, ...ys.map((y) => ({ value: y, label: y }))];
  }, [rows]);
  const [pickedYear, setYear] = useState(null);
  const year = pickedYear ?? (years[1] ? years[1].value : '');

  const filtered = useMemo(
    () => rows.filter((r) => !year || r.monthKey.startsWith(year)).map((r) => ({ ...r, monthLabel: api.reportMonthLabel(r.monthKey) })),
    [rows, year]
  );

  const summary = useMemo(() => {
    const sum = (f) => filtered.reduce((acc, r) => acc + r[f], 0);
    return { rides: sum('rides'), grossFare: sum('grossFare'), platformFee: sum('platformFee'), profit: sum('profit'), discounts: sum('discounts') };
  }, [filtered]);

  const columns = [
    { title: 'Mes', field: 'monthLabel', customSort: (a, b) => a.monthKey.localeCompare(b.monthKey) },
    {
      title: 'Viajes',
      field: 'rides',
      type: 'numeric',
      render: (r) => <C2Cell primary={r.rides} secondary={r.cancelledWithFee ? `${r.cancelledWithFee} cancelados con cargo` : null} />,
    },
    { title: 'Cancelados con cargo', field: 'cancelledWithFee', hidden: true, export: true },
    copColumn('Valor de los viajes', 'grossFare'),
    copColumn('Parte de conductores', 'driverShare'),
    copColumn('Comisión de flotas', 'fleetFee'),
    copColumn('Comisión plataforma', 'platformFee'),
    copColumn('Descuentos', 'discounts'),
    copColumn('Cargos por cancelación', 'cancellationFees', {
      render: (r) => <C2Cell primary={formatCop(r.cancellationFees)} secondary="Se acreditan al conductor" strong={false} />,
    }),
    {
      title: 'Utilidad',
      field: 'profit',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.profit)} color={r.profit < 0 ? '#B91C1C' : '#15803D'} />,
    },
  ];

  const periodCaption = year || 'Todos los años';

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={RouteOutlinedIcon} label="Viajes completados" value={summary.rides} caption={periodCaption} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={ReceiptLongOutlinedIcon} label="Valor de los viajes" value={formatCop(summary.grossFare)} caption={periodCaption} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={AccountBalanceOutlinedIcon} label="Comisión plataforma" value={formatCop(summary.platformFee)} caption={`Descuentos ${formatCop(summary.discounts)}`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={TrendingUpOutlinedIcon} label="Utilidad" value={formatCop(summary.profit)} caption="Comisión plataforma − descuentos" accent="#16A34A" />
        </Grid>
      </Grid>
      <C2ReportTable
        title="Ganancias de la plataforma"
        subtitle="Resumen mensual de viajes completados. La utilidad es la comisión de la plataforma menos los descuentos; los cargos por cancelación van al conductor."
        columns={columns}
        data={filtered}
        loading={loading}
        emptyIcon={ReceiptLongOutlinedIcon}
        emptyTitle="Sin viajes en este periodo"
        emptyMessage="Cuando se completen viajes, el resumen mensual aparecerá aquí."
        csvFileName="ganancias_plataforma.csv"
        toolbarExtras={[
          <C2Select key="y" id="platform-earning-year" label="Año" value={year} onChange={(e) => setYear(e.target.value)} options={years} minWidth={150} />,
        ]}
      />
    </>
  );
}
